// =============================================================================
// Admin Tax Exemptions — review queue for merchant certificates
// =============================================================================

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import {
  TaxExemptionReviewStatus,
  type ListTaxExemptionDocumentsAdminResponse,
} from '@tkpod/shared';
import { Button, Card, Spinner, useConfirm } from '@tkpod/ui';

export function AdminTaxExemptionsPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [status, setStatus] = useState<string>(TaxExemptionReviewStatus.PENDING);
  const [page, setPage] = useState(1);

  const query = useQuery<ListTaxExemptionDocumentsAdminResponse>({
    queryKey: ['admin', 'tax-exemptions', { status, page }],
    queryFn: () => {
      const qs = new URLSearchParams({ page: String(page), limit: '25' });
      if (status) qs.set('status', status);
      return api.get<ListTaxExemptionDocumentsAdminResponse>(
        `/api/v1/admin/tax-exemptions?${qs}`,
      );
    },
  });

  const reviewMutation = useMutation({
    mutationFn: (input: {
      id: string;
      status: 'APPROVED' | 'REJECTED';
      reviewNote?: string;
    }) =>
      api.patch(`/api/v1/admin/tax-exemptions/${input.id}/review`, {
        status: input.status,
        reviewNote: input.reviewNote,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tax-exemptions'] });
    },
  });

  async function openFile(id: string) {
    const res = await api.get<{ data: { url: string } }>(
      `/api/v1/admin/tax-exemptions/${id}/file`,
    );
    window.open(res.data.url, '_blank', 'noopener,noreferrer');
  }

  async function decide(id: string, next: 'APPROVED' | 'REJECTED') {
    const ok = await confirm({
      title: next === 'APPROVED' ? 'Approve certificate' : 'Reject certificate',
      description:
        next === 'APPROVED'
          ? 'This records an Admin review. Tax is not calculated this wave — approval is documentation only.'
          : 'The merchant will see this certificate as rejected.',
      confirmLabel: next === 'APPROVED' ? 'Approve' : 'Reject',
      tone: next === 'REJECTED' ? 'danger' : 'default',
    });
    if (ok) reviewMutation.mutate({ id, status: next });
  }

  return (
    <Card padding="none">
      {dialog}
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">
            Tax exemptions
          </h1>
          <p className="text-xs text-text-tertiary">
            Review merchant resale certificates. Approval does not charge or
            waive tax this wave.
          </p>
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm"
        >
          <option value={TaxExemptionReviewStatus.PENDING}>Pending</option>
          <option value={TaxExemptionReviewStatus.APPROVED}>Approved</option>
          <option value={TaxExemptionReviewStatus.REJECTED}>Rejected</option>
          <option value="">All</option>
        </select>
      </div>

      {query.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : query.isError ? (
        <p className="px-5 py-6 text-sm text-rose-600">
          {query.error instanceof Error
            ? query.error.message
            : 'Unable to load certificates.'}
        </p>
      ) : !query.data?.items.length ? (
        <p className="px-5 py-12 text-center text-sm text-text-secondary">
          No certificates in this filter.
        </p>
      ) : (
        <>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Organization</th>
                <th className="px-5 py-2 font-semibold">File</th>
                <th className="px-5 py-2 font-semibold">Kind</th>
                <th className="px-5 py-2 font-semibold">States</th>
                <th className="px-5 py-2 font-semibold">Status</th>
                <th className="px-5 py-2 font-semibold">Uploaded</th>
                <th className="px-5 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((doc) => (
                <tr key={doc.id} className="border-b border-border/60">
                  <td className="px-5 py-3">{doc.organizationName}</td>
                  <td className="px-5 py-3">
                    <button
                      type="button"
                      className="text-brand-600 hover:underline"
                      onClick={() => void openFile(doc.id)}
                    >
                      {doc.originalFilename}
                    </button>
                  </td>
                  <td className="px-5 py-3">{doc.kind}</td>
                  <td className="px-5 py-3">
                    {doc.states.length ? doc.states.join(', ') : '—'}
                  </td>
                  <td className="px-5 py-3">{doc.reviewStatus}</td>
                  <td className="px-5 py-3 text-xs text-text-tertiary">
                    {new Date(doc.createdAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3">
                    {doc.reviewStatus === 'PENDING' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={reviewMutation.isPending}
                          onClick={() => void decide(doc.id, 'APPROVED')}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={reviewMutation.isPending}
                          onClick={() => void decide(doc.id, 'REJECTED')}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-text-secondary">
            <span>
              Page {page} · {query.data.total} total
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={page * query.data.limit >= query.data.total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
